/** Copyright (C) 2023 Trend Micro Inc. All rights reserved. */

import api from '@redux/apis/baseApi'
import { transformResponseWithHttpStatus } from '@redux/apis/sharedUtils'
import { API_RESOURCE } from '@constants/api'

const AWS_SCANNER_CACHE_NAME = 'AWS_SCANNER'
const AWS_STORAGE_CACHE_NAME = 'AWS_STORAGE'
const AZURE_SCANNER_CACHE_NAME = 'AZURE_SCANNER'
const AZURE_STORAGE_CACHE_NAME = 'AZURE_STORAGE'
const GCP_SCANNER_CACHE_NAME = 'GCP_SCANNER'
const GCP_STORAGE_CACHE_NAME = 'GCP_STORAGE'

const AWS_SCANNER_LIST_CACHE_NAME = 'AWS_SCANNER_LIST'
const AZURE_SCANNER_LIST_CACHE_NAME = 'AZURE_SCANNER_LIST'
const GCP_SCANNER_LIST_CACHE_NAME = 'GCP_SCANNER_LIST'

const AWS_STORAGE_LIST_CACHE_NAME = 'AWS_STORAGE_LIST'
const AZURE_STORAGE_LIST_CACHE_NAME = 'AZURE_STORAGE_LIST'
const GCP_STORAGE_LIST_CACHE_NAME = 'GCP_STORAGE_LIST'

const STORAGE_LIST_CACHE_NAME: string = '' // Assign the scanner id to the storage list cache name

type ScannerCacheType =
  | typeof AWS_SCANNER_CACHE_NAME
  | typeof AZURE_SCANNER_CACHE_NAME
  | typeof GCP_SCANNER_CACHE_NAME

type StorageCacheType =
  | typeof AWS_STORAGE_CACHE_NAME
  | typeof AZURE_STORAGE_CACHE_NAME
  | typeof GCP_STORAGE_CACHE_NAME

type ScannerListCacheID =
  | typeof AWS_SCANNER_LIST_CACHE_NAME
  | typeof AZURE_SCANNER_LIST_CACHE_NAME
  | typeof GCP_SCANNER_LIST_CACHE_NAME
  | string

const getStacksQueryHelper = (type: Response.Stack.StackType) => ({
  query: ({
    provider,
    status,
    sortBy = 'name',
    sortOrder,
    cursor,
    scannerStack,
  }: Request.StorageStackListParams extends Pick<
    Request.StorageStackListParams,
    'scannerStack'
  >
    ? Request.StorageStackListParams
    : Request.StackListParams) => {
    const qs = Object.entries({
      type,
      provider,
      status,
      sort: 'name:asc',
      cursor,
      scannerStack,
    }).reduce((acc, [key, val]) => {
      if (key === 'sort' && sortOrder) {
        acc.push(`${key}=${sortBy}:${sortOrder}`)
      } else if (val) {
        acc.push(`${key}=${val}`)
      }

      return acc
    }, [])

    return {
      url: `${API_RESOURCE.STACKS}${qs.length > 0 && `?${qs.join('&')}`}`,
    }
  },
})

const getScannerStacksQuery = getStacksQueryHelper('scanner')
const getStorageStacksQuery = getStacksQueryHelper('storage')

const stackApi = api.injectEndpoints({
  endpoints: build => ({
    getScannerStacks: build.query<
      Response.Stack.ScannerListAPI,
      Omit<Request.StackListParams, 'type'>
    >(getScannerStacksQuery),
    getStorageStacks: build.query<
      Response.Stack.StorageListAPI,
      Omit<Request.StorageStackListParams, 'type'>
    >(getStorageStacksQuery),
    getStack: build.query<
      Response.CustomResponse<Response.Stack.AnyStack>,
      string
    >({
      query: stackID => ({
        url: `${API_RESOURCE.STACKS}/${stackID}`,
      }),
      transformResponse:
        transformResponseWithHttpStatus<Response.Stack.AnyStack>,
    }),
    /**
     * It's not required to reset the cache at this point since the stacks API doesn't mutate immediately.
     * Reset stacks API after `describe stack API` responses if the latest stack list is required.
     * e.g dispatch(resetScannerStacksCacheAction()) or dispatch(resetStorageStacksCacheAction())
     */
    postStack: build.mutation<
      Response.Stack.CreateStack,
      Request.CreateStackAPIParam
    >({
      query({ ...body }) {
        return {
          url: API_RESOURCE.STACKS,
          method: 'POST',
          body,
        }
      },
    }),
    deleteStack: build.mutation<
      Response.CustomResponse<Response.Stack.DeleteStack>,
      Request.DeleteStackAPIParam
    >({
      query: ({ stackID }) => ({
        url: `${API_RESOURCE.STACKS}/${stackID}`,
        method: 'DELETE',
      }),
      transformResponse: transformResponseWithHttpStatus<null>,
    }),
  }),
})

function getScannerTags(provider: Response.Stack.StackItemProvider) {
  let cacheType: ScannerCacheType
  let cacheListType: { type: ScannerCacheType; id: ScannerListCacheID }

  switch (provider) {
    case 'aws':
      cacheType = AWS_SCANNER_CACHE_NAME
      cacheListType = {
        type: AWS_SCANNER_CACHE_NAME,
        id: AWS_SCANNER_LIST_CACHE_NAME,
      }
      break
    case 'azure':
      cacheType = AZURE_SCANNER_CACHE_NAME
      cacheListType = {
        type: AZURE_SCANNER_CACHE_NAME,
        id: AZURE_SCANNER_LIST_CACHE_NAME,
      }
      break
    case 'gcp':
      cacheType = GCP_SCANNER_CACHE_NAME
      cacheListType = {
        type: GCP_SCANNER_CACHE_NAME,
        id: GCP_SCANNER_LIST_CACHE_NAME,
      }
      break
    default:
  }

  return {
    cacheType,
    cacheListType,
  }
}

function providesScannerList(
  result: Response.Stack.ScannerListAPI,
  arg?: Partial<Request.StackListParams>
): {
  type: ScannerCacheType
  id: ScannerListCacheID
}[] {
  const { provider } = arg
  const { cacheType, cacheListType } = getScannerTags(provider)

  return result?.stacks
    ? [
        cacheListType,
        ...result.stacks.map(({ stackID }) => ({
          type: cacheType,
          id: stackID,
        })),
      ]
    : [cacheListType]
}

function getStorageListTag(provider: Response.Stack.StackItemProvider) {
  let cacheListType: { type: StorageCacheType; id: string }

  switch (provider) {
    case 'aws':
      cacheListType = {
        type: AWS_STORAGE_CACHE_NAME,
        id: AWS_STORAGE_LIST_CACHE_NAME,
      }
      break
    case 'azure':
      cacheListType = {
        type: AZURE_STORAGE_CACHE_NAME,
        id: AZURE_STORAGE_LIST_CACHE_NAME,
      }
      break
    case 'gcp':
      cacheListType = {
        type: GCP_STORAGE_CACHE_NAME,
        id: GCP_STORAGE_LIST_CACHE_NAME,
      }
      break
    default:
  }

  return cacheListType
}

function providesStorageList(
  result: Response.Stack.StorageListAPI,
  arg?: Partial<Request.StorageStackListParams>
) {
  const { provider } = arg
  const cacheListType = getStorageListTag(provider)

  return result?.stacks
    ? [
        cacheListType,
        ...result.stacks.map(({ stackID }) => ({
          type: cacheListType.type,
          id: stackID,
        })),
      ]
    : [cacheListType]
}

function invalidateTagHelper(
  stackID: string,
  provider: Response.Stack.StackItemProvider,
  scannerStack: string
) {
  if (scannerStack) {
    switch (provider) {
      case 'aws':
        return [{ type: AWS_STORAGE_CACHE_NAME, id: stackID }]
      case 'azure':
        return [{ type: AZURE_STORAGE_CACHE_NAME, id: stackID }]
      case 'gcp':
        return [{ type: GCP_STORAGE_CACHE_NAME, id: stackID }]
      default:
        return
    }
  }

  switch (provider) {
    case 'aws':
      return [{ type: AWS_SCANNER_CACHE_NAME, id: stackID }]
    case 'azure':
      return [{ type: AZURE_SCANNER_CACHE_NAME, id: stackID }]
    case 'gcp':
      return [{ type: GCP_SCANNER_CACHE_NAME, id: stackID }]
    default:
  }
}

stackApi.enhanceEndpoints({
  addTagTypes: [
    AWS_SCANNER_CACHE_NAME,
    AWS_SCANNER_LIST_CACHE_NAME,
    AZURE_SCANNER_CACHE_NAME,
    AZURE_SCANNER_LIST_CACHE_NAME,
    GCP_SCANNER_CACHE_NAME,
    GCP_SCANNER_LIST_CACHE_NAME,
    AWS_STORAGE_CACHE_NAME,
    AZURE_STORAGE_CACHE_NAME,
    GCP_STORAGE_CACHE_NAME,
    STORAGE_LIST_CACHE_NAME,
  ],
  endpoints: {
    getScannerStacks(endpoint) {
      endpoint.providesTags = (
        result: Response.Stack.ScannerListAPI,
        err,
        arg
      ) =>
        providesScannerList(result, {
          provider: arg?.provider,
        })
    },
    getStorageStacks(endpoint) {
      endpoint.providesTags = (
        result: Response.Stack.StorageListAPI,
        err,
        arg
      ) =>
        providesStorageList(result, {
          scannerStack: arg?.scannerStack,
          provider: arg?.provider,
        })
    },
    getStack: {
      extraOptions: { maxRetries: 0 },
    },
    deleteStack: {
      invalidatesTags: (result, err, arg) =>
        invalidateTagHelper(arg.stackID, arg.provider, arg.scannerStack),
    },
  },
})

/**
 * Auto Re-fetch will happen once the cache reset.
 * e.g dispatch(resetScannerStacksCacheAction())
 */
export const resetScannerStacksCacheAction = (
  provider: Response.Stack.StackItemProvider
) => {
  let cacheId: ScannerListCacheID
  let cacheType: ScannerCacheType

  switch (provider) {
    case 'aws':
      cacheId = AWS_SCANNER_LIST_CACHE_NAME
      cacheType = AWS_SCANNER_CACHE_NAME
      break
    case 'azure':
      cacheId = AZURE_SCANNER_LIST_CACHE_NAME
      cacheType = AZURE_SCANNER_CACHE_NAME
      break
    case 'gcp':
      cacheId = GCP_SCANNER_LIST_CACHE_NAME
      cacheType = GCP_SCANNER_CACHE_NAME
      break
    default:
  }

  return stackApi.util.invalidateTags([
    // @ts-ignore
    { type: cacheType, id: cacheId },
  ])
}

/**
 * Auto Re-fetch will happen once the cache reset.
 * e.g dispatch(resetStorageStacksCacheAction())
 */
export const resetStorageStacksCacheAction = (
  provider: Response.Stack.StackItemProvider,
  scannerStack: string
) => {
  let cacheId: StorageCacheType

  switch (provider) {
    case 'aws':
      cacheId = AWS_STORAGE_CACHE_NAME
      break
    case 'azure':
      cacheId = AZURE_STORAGE_CACHE_NAME
      break
    case 'gcp':
      cacheId = GCP_STORAGE_CACHE_NAME
      break
    default:
  }

  return stackApi.util.invalidateTags([
    // @ts-ignore
    { type: scannerStack, id: cacheId },
  ])
}

export default stackApi
